import {Injectable, Injector} from "@angular/core";

import {BaseListService, BatchResponse, ListRecordFetcher} from "app/services/domain/base.service";
import {BillingServiceAccountScope} from "app/services/domain/billing/billing-types";
import * as Models from "app/services/proxy/model/models";

import {CacheItemBlock, IFixupPrototype} from "framework/services/cache.service";
import {DedupeInterceptorStore, getPathSuffix} from "framework/services/dedupe-interceptor/dedupe-interceptor-store";
import {Lookup, LookupHelpers} from "framework/services/utils.service";

@Injectable()
export class BillingMetricsListService extends BaseListService<any, Models.BatchRequest, Models.BillingMetrics>
// should extend BaseListService<Models.BillingMetricListRequest, ...> but BillingMetricListRequest has no list_context property
{
    private readonly m_autoBatcher: ListRecordFetcher<Models.BillingMetrics, this> = this.cachedAutoBatcher();

    public static readonly LIST_TTL: number = 5 * 60_000;

    constructor(inj: Injector,
                dedupeInterceptorRegistry: DedupeInterceptorStore)
    {
        super(inj, Models.BillingMetrics, 500);

        const pathPrefix           = "/v2/billing_metrics/";
        const dedupeTtl            = (method: string,
                                      path: string) =>
        {
            if (method == "POST")
            {
                path = getPathSuffix(path, pathPrefix);
                if (path?.endsWith("list")) return BillingMetricsListService.LIST_TTL;
            }

            return undefined;
        };
        const isInvalidatePOSTPath = (path: string) =>
        {
            path = getPathSuffix(path, pathPrefix);
            switch (path)
            {
                case "access/create":
                    return true;

                case "list":
                case "access/list":
                case "batch":
                case "access/batch":
                    return false;

                default:
                    return path?.startsWith("access/item");
            }
        };

        dedupeInterceptorRegistry.registerDedupeTtl(dedupeTtl);
        dedupeInterceptorRegistry.registerInvalidatePOSTPath(pathPrefix, isInvalidatePOSTPath);
    }

    protected createBatchRequest(listReq: Models.BillingMetricListRequest,
                                 ids: string[]): Models.BatchRequest
    {
        return Models.BatchRequest.newInstance({ids: ids});
    }

    protected async getBatchResponseImpl(req: Models.BatchRequest): Promise<BatchResponse<Models.BillingMetrics>>
    {
        if (!req?.ids) return null;

        let batchResult: Lookup<Models.BillingMetrics> = {};
        if (req.ids.length)
        {
            const response = await this.domain.apis.billingMetric.billingMetricsBatch(req);
            batchResult    = LookupHelpers.from(response, (metric) => metric.sys_id);
        }

        return {results: batchResult};
    }

    public batchAsLookup(req: Models.BatchRequest,
                         useCache: boolean = true): Promise<Lookup<Models.BillingMetrics>>
    {
        return useCache ? this.m_autoBatcher.getBatchAsLookup(req.ids) : super.batchAsLookup(req);
    }

    public cachedAutoBatcher<T extends BaseListService<any, Models.BatchRequest, Models.BillingMetrics> = this>(block?: CacheItemBlock): BillingMetricFetcher<T>
    {
        const listService: BaseListService<any, Models.BatchRequest, Models.BillingMetrics> = this;
        return new BillingMetricFetcher(<T>listService, this.m_fixupValue, block);
    }

    public list(listReq: Models.BillingMetricListRequest): Promise<Models.ListResponse>
    {
        return this.domain.apis.billingMetric.billingMetricsList(listReq);
    }

    // Per-dashboard shared promise so all widgets reuse one network call.
    private readonly m_dashboardMetricsCache = new Map<string, Promise<Models.BillingMetrics[]>>();

    public getDashboardMetrics(dashboardId: string,
                                cloud: Models.CloudServiceName | null): Promise<Models.BillingMetrics[]>
    {
        const existing = this.m_dashboardMetricsCache.get(dashboardId);
        if (existing) return existing;

        const promise = this.resolveDashboardScope(cloud).then(scope => this.getMetrics(scope));
        this.m_dashboardMetricsCache.set(dashboardId, promise);
        return promise;
    }

    public clearDashboardMetricsCache(dashboardId: string): void
    {
        this.m_dashboardMetricsCache.delete(dashboardId);
    }

    private async resolveDashboardScope(cloud: Models.CloudServiceName | null): Promise<BillingServiceAccountScope>
    {
        const accounts = await this.domain.filterOptions.resolver.filterCloudAccounts(null, null, [cloud], null, null, null, null);
        const ids      = accounts.map(a => a.cloud_account_id).sort();
        return {service_account_ids: ids};
    }

    // IDs are sorted so DedupeInterceptorStore deduplicates (in-flight + LIST_TTL cache) regardless of call-site ordering.
    public async getMetrics(serviceAccountScope?: BillingServiceAccountScope | null): Promise<Models.BillingMetrics[]>
    {
        const request = new Models.BillingMetricListRequest();

        // Skip scope when service_account_ids is empty — sending [] returns only the backend fallback metric.
        const hasValidScope = serviceAccountScope != null
                              && (serviceAccountScope.descendant_service_account_query
                                  || serviceAccountScope.service_account_ids?.length);
        if (hasValidScope)
        {
            const sortedFlatScope = serviceAccountScope.service_account_ids?.slice()
                                                       .sort();
            request.filters = Models.BillingMetricFilters.newInstance({
                                                                          ...serviceAccountScope,
                                                                          ...(sortedFlatScope && {service_account_ids: sortedFlatScope})
                                                                      });
        }

        const ids = await this.listAll(request);
        if (!ids.length) return [];

        return Object.values(await this.batchAsLookup(Models.BatchRequest.newInstance({ids})) ?? {})
                     .filter((m): m is Models.BillingMetrics => !!m);
    }

    // to support standard getAll pattern that other list/batch services/apis support
    public async getAll(listReq: Models.BillingMetricListRequest): Promise<[string, Models.BillingMetrics][]>
    {
        const ids   = await this.listAll(listReq);
        const batch = await this.batchAsLookup(Models.BatchRequest.newInstance({ids: ids}));
        if (!batch) return null;

        let result: [string, Models.BillingMetrics][] = [];
        for (const id of ids)
        {
            result.push([
                            id,
                            batch[id]
                        ]);
        }
        return result;
    }
}

class BillingMetricFetcher<T extends BaseListService<any, Models.BatchRequest, Models.BillingMetrics>> extends ListRecordFetcher<Models.BillingMetrics, T>
{
    readonly billingMetricListSvc: BillingMetricsListService;

    constructor(service: T,
                fixup: IFixupPrototype<Models.BillingMetrics>,
                block?: CacheItemBlock)
    {
        super(service, fixup, block);

        if (service instanceof BillingMetricsListService) this.billingMetricListSvc = service;
    }

    protected performBatchRequest(records: Models.RecordIdentity[]): Promise<Lookup<Models.BillingMetrics>>
    {
        if (this.billingMetricListSvc)
        {
            const ids = records.map((record) => record?.sysId);
            return this.billingMetricListSvc.batchAsLookup(Models.BatchRequest.newInstance({ids: ids}), false);
        }

        return super.performBatchRequest(records);
    }
}
